import React from "react";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">

      <h1>Privacy Policy</h1>
      <p style={{textAlign: 'center', margin: '0px'}}>Effective Date: 5 Dec 2025</p>

      <h2>1. Introduction</h2>
      <p>
        Welcome to Icatalog App ("we," "our," or "us"). We respect your privacy and
        are committed to protecting your personal information. This Privacy Policy explains:
      </p>

      <ul>
        <li>What data we collect</li>
        <li>Why we collect it</li>
        <li>How we use it</li>
        <li>How we protect it</li>
        <li>Your rights regarding your data</li>
      </ul>

      <p>
        By using our App, you agree to the collection and use of your data as described in this
        Privacy Policy.
      </p>

      <h2>2. Information We Collect</h2>
      <p>We collect the following data:</p>

      <h3>A. Personal Information (Provided by Users)</h3>
      <p>When you register or log in, we collect:</p>
      <ul>
        <li>First Name</li>
        <li>Last Name</li>
        <li>Email Address</li>
        <li>Mobile Number</li>
        <li>Password (encrypted and stored securely)</li>
        <li>Address</li>
        <li>Country</li>
        <li>State</li>
        <li>City</li>
        <li>Zip Code</li>
      </ul>

      <h3>B. Device Information (Collected Automatically)</h3>
      <p>To ensure app compatibility and security, we collect:</p>
      <ul>
        <li>Brand: (e.g., Samsung, Apple)</li>
        <li>Model: (e.g., Galaxy S23, iPhone 14)</li>
        <li>Manufacturer: (e.g., Samsung Electronics, Apple Inc.)</li>
        <li>OS Version: (e.g., Android 13, iOS 17)</li>
        <li>OS Version Code: (e.g., SDK 34 for Android)</li>
        <li>Device Type: (e.g., "android" or "iOS")</li>
      </ul>

      <h3>C. User Identifiers</h3>
      <p>To provide a personalized experience, we collect:</p>
      <ul>
        <li>OneSignal UID: A unique identifier for push notifications.</li>
        <li>Device ID: A unique identifier for managing user sessions.</li>
      </ul>

      <h3>D. Usage Data & Crash Reports</h3>
      <p>We use Firebase for performance monitoring and error tracking. This may include:</p>
      <ul>
        <li>Error logs</li>
        <li>Crash reports</li>
        <li>App performance data</li>
      </ul>

      <p>
        Even if Firebase Crashlytics is not explicitly configured, Firebase may still collect
        diagnostic information for troubleshooting purposes.
      </p>

      <p>This data is only used for app improvements and is not shared for advertising purposes.</p>

      <h2>3. How We Use Your Information</h2>
      <p>We use collected data for:</p>

      <ul>
        <li>Authentication & Security: Verify accounts and prevent fraud.</li>
        <li>App Performance: Improve user experience based on device compatibility.</li>
        <li>Push Notifications: Send updates and promotional alerts via OneSignal.</li>
        <li>Bug Fixing & Crash Reports: Identify and resolve technical issues.</li>
        <li>Order Management: Process orders and transactions.</li>
        <li>Compliance: Ensure legal and regulatory compliance.</li>
      </ul>

      <p>We do not sell or share your personal data with advertisers.</p>

      <h2>4. How We Share Your Information</h2>
      <p>We do not sell your personal data. However, we may share data in the following cases:</p>

      <ul>
        <li>With Service Providers: We use third-party services (e.g., Firebase, OneSignal) to improve our App.</li>
        <li>With Legal Authorities: If required by law, we may disclose your information.</li>
      </ul>

      <h2>5. Data Security</h2>
      <p>We implement security measures to protect your data:</p>

      <ul>
        <li>✅ Encryption: Passwords are encrypted before storage.</li>
        <li>✅ Secure Storage: Sensitive data is stored securely.</li>
        <li>✅ Restricted Access: Only authorized personnel can access user data.</li>
      </ul>

      <p>
        We take appropriate security measures to protect your data. However, no method of transmission
        over the internet or electronic storage is 100% secure. We encourage you to keep your login
        credentials confidential and take steps to protect your account from unauthorized access.
      </p>

      <h2>6. Your Rights & Choices</h2>
      <p>You have the following rights:</p>
      <ul>
        <li>Correction: Request corrections to inaccurate data.</li>
        <li>Deletion: Request data deletion (subject to legal obligations).</li>
        <li>
          Notification Preferences: You cannot opt out of receiving push notifications within the app.
          However, you can manage notification preferences through your device settings.
        </li>
      </ul>

      <p>To exercise these rights, contact us at support@orail.in.</p>

      <h2>7. Data Retention Policy</h2>
      <p>We retain all collected data until the user deletes their account. This includes, but is not limited to:</p>

      <ul>
        <li>Personal Information: First name, last name, email, address, city, country, state, zip code, mobile number, encrypted password.</li>
        <li>Device Information: Brand, model, manufacturer, OS version, OS version code, device type.</li>
        <li>User Identifiers: OneSignal UID (for push notifications), Device ID.</li>
        <li>Usage Data: App activity logs, error logs, and crash reports (collected via Firebase).</li>
        <li>Order & Transaction Data: If applicable, any order history or transaction details linked to the user.</li>
      </ul>

      <p>
        Once a user requests account deletion, all associated data is permanently erased, except where
        retention is required by law or for fraud prevention.
      </p>

      <p>Users can request account and data deletion by contacting support@orail.in.</p>

      <h2>8. Third-Party Services</h2>

      <p>
        Our App uses third-party services to improve functionality, performance, and user experience.
        These services may collect and process data per their own privacy policies.
      </p>

      <p>The third-party services we use include:</p>

      <ul>
        <li>OneSignal (for push notifications) – Privacy Policy</li>
        <li>Firebase (for analytics and crash reporting) – Privacy Policy</li>
      </ul>

      <p>
        These services may collect device and usage data to enhance performance and stability. We do
        not share personal information with these services for advertising purposes.
      </p>

      <p>For more details, please refer to their respective privacy policies.</p>

      <h2>9. Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy. Users will be notified of changes via:</p>

      <ul>
        <li>In-app notifications</li>
        <li>Emails (if applicable)</li>
      </ul>

      <p>We encourage users to review the Privacy Policy regularly.</p>

      <h2>10. Contact Us</h2>
      <p>Icatalog App</p>
      <p>
        Address: ITC D-Block, G 19-22A, International Trade Center, Majura Gate, Ring Road,
        Surat – 395002. Gujarat (INDIA).
      </p>
      <p>Email: support@orail.in</p>

    </div>
  );
};

export default PrivacyPolicy;
